<template>
	<v-container class="dashboard-container">
		<v-row class="contentRow">
			<v-col cols="12">
				<div class="d-flex align-center justify-center mb-5 header-row">
					<h1 class="primary--text mb-0">Full Process View</h1>
					<v-btn
						v-if="hasProcesses"
						class="download-pdf-btn"
						small
						outlined
						color="primary"
						:loading="downloadingPdf"
						:disabled="downloadingPdf"
						@click="downloadPdf"
					>
						<v-icon small left>fas fa-download</v-icon>
						Download PDF
					</v-btn>
				</div>

				<v-alert v-if="!loading && !hasProcesses" type="info" outlined>
					No processes have been added yet.
				</v-alert>

				<template v-for="group in groups">
					<div :key="group.type + '-header'">
						<h3 align="center" class="section-title " :class="{ 'mt-15': group.type === 'sub' }">
							{{ group.label }}
						</h3>

						<v-card
							v-for="(row, index) in group.rows"
							:key="group.type + '-' + (row.ID || index)"
							class="process-entry-card pa-4 mb-8"
							outlined
						>
							<div class="d-flex align-center justify-space-between mb-2">
								<h3><u>Process {{ index + 1 }}</u></h3>
								<div>
									<v-btn
										v-if="editingRowId !== row.ID"
										small
										outlined
										color="primary"
										:disabled="editingRowId !== null"
										@click="startEdit(row)"
									>
										<v-icon small left>fas fa-pen</v-icon>
										Edit
									</v-btn>
									<template v-else>
										<v-btn
											small
											outlined
											color="success"
											class="mr-2"
											:loading="savingRowId === row.ID"
											@click="saveEdit(row, group.type)"
										>
											Save
										</v-btn>
										<v-btn small outlined color="error" :disabled="savingRowId === row.ID" @click="cancelEdit(row)">
											Cancel
										</v-btn>
									</template>
								</div>
							</div>

							<p v-if="editingRowId === row.ID && editType !== group.type" class="type-change-hint">
								<v-icon x-small>fas fa-circle-info</v-icon> Saving will move this process to
								{{ editType === 'main' ? 'Main' : 'Sub' }} Processes.
							</p>

							<div class="field-row">
								<div class="field-block">
									<p class="field-label">1. Process Name :-</p>
									<v-text-field
										v-if="editingRowId === row.ID"
										v-model="row.Name"
										dense
										outlined
										hide-details
									/>
									<p v-else class="field-value">{{ getField(row, 'name') || '—' }}</p>
								</div>
								<div class="field-block">
									<p class="field-label">2. Process Owner :-</p>
									<v-text-field
										v-if="editingRowId === row.ID"
										v-model="row.Owner"
										dense
										outlined
										hide-details
									/>
									<p v-else class="field-value">{{ getField(row, 'owner') || '—' }}</p>
								</div>
								<div class="field-block">
									<p class="field-label">3. Process Type :-</p>
									<v-select
										v-if="editingRowId === row.ID"
										v-model="editType"
										:items="processTypeOptions"
										item-text="text"
										item-value="value"
										dense
										outlined
										hide-details
									/>
									<p v-else class="field-value">{{ group.label.replace(' Processes', '') }}</p>
								</div>
							</div>

							<div class="field-block mb-4">
								<p class="field-label">4. Description of the Process :-</p>
								<v-text-field
									v-if="editingRowId === row.ID"
									v-model="editShort"
									dense
									outlined
									hide-details
								/>
								<p v-else class="field-value">{{ getField(row, 'short') || '—' }}</p>
							</div>

							<template v-if="editingRowId === row.ID || getSteps(row).length > 0">
								<p class="field-label">Process Steps :-</p>
								<p v-if="editingRowId === row.ID" class="text-left mb-2" style="font-size: 13px">
									<i><v-icon x-small>fas fa-circle-info</v-icon> Add at least 3 steps describing this process in order, up to a maximum of 10.</i>
								</p>

								<div class="description-grid">
									<template v-if="editingRowId === row.ID">
										<div
											v-for="(step, sIndex) in editSteps"
											:key="group.type + '-editstep-' + index + '-' + sIndex"
											class="chevron-box"
										>
											<div class="chevron-inner">
												<v-textarea
													v-model="editSteps[sIndex]"
													flat
													solo
													dense
													hide-details
													rows="3"
													no-resize
													class="chevron-textarea"
													:placeholder="'Describe step ' + (sIndex + 1)"
												></v-textarea>
												<v-btn
													v-if="sIndex >= 3"
													icon
													x-small
													class="step-remove-btn"
													@click="removeEditStep(sIndex)"
													title="Remove step"
												>
													<v-icon x-small>fas fa-xmark</v-icon>
												</v-btn>
											</div>
										</div>
									</template>
									<template v-else>
										<div
											v-for="(step, sIndex) in getSteps(row)"
											:key="group.type + '-step-' + index + '-' + sIndex"
											class="chevron-box"
										>
											<div class="chevron-inner">
												<p class="step-text-value">{{ step }}</p>
											</div>
										</div>
									</template>
								</div>

								<div v-if="editingRowId === row.ID" class="mb-4">
									<v-btn small outlined color="primary" :disabled="editSteps.length >= 10" @click="addEditStep">
										+ Add Step ({{ editSteps.length }}/10)
									</v-btn>
								</div>
							</template>

							<div class="field-row mt-4">
								<div class="field-block">
									<p class="field-label">5. Input (What is the input and from Who) :-</p>
									<v-text-field
										v-if="editingRowId === row.ID"
										v-model="row.Input"
										dense
										outlined
										hide-details
									/>
									<p v-else class="field-value">{{ getField(row, 'input') || '—' }}</p>
								</div>
								<div class="field-block">
									<p class="field-label">6. Output (What is the output and who is the Receiver) :-</p>
									<v-text-field
										v-if="editingRowId === row.ID"
										v-model="row.Output"
										dense
										outlined
										hide-details
									/>
									<p v-else class="field-value">{{ getField(row, 'output') || '—' }}</p>
								</div>
							</div>
						</v-card>
					</div>
				</template>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
import LoadingState from "../consts/LoadingState"
import { riskAPI } from "../api/csr/risk"
import jsPDF from "jspdf"

export default {
	data() {
		return {
			loading: false,
			downloadingPdf: false,
			mainProcesses: [],
			subProcesses: [],
			processTypeOptions: [
				{ text: "Main", value: "main" },
				{ text: "Sub", value: "sub" },
			],
			editingRowId: null,
			savingRowId: null,
			rowBackup: null,
			editSteps: [],
			editType: null,
			editShort: "",
		}
	},
	computed: {
		hasProcesses() {
			return this.mainProcesses.length > 0 || this.subProcesses.length > 0
		},
		groups() {
			return [
				{ type: "main", label: "MAIN PROCESSES", rows: this.mainProcesses },
				{ type: "sub", label: "SUB PROCESSES", rows: this.subProcesses },
			].filter(group => group.rows.length > 0)
		},
	},
	mounted() {
		this.loadProcesses()
	},
	methods: {
		loadProcesses() {
			this.loading = true
			riskAPI.viewProcess()
				.then(result => {
					const rows = (result && result.length > 0 && Array.isArray(result[0])) ? result[0] : []

					this.mainProcesses = rows.filter(row => this.getRowType(row) === "main")
					this.subProcesses = rows.filter(row => this.getRowType(row) === "sub")

					this.$store.dispatch("loading/" + LoadingState.ACTION_END_LOADING, null, { root: true })
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
				.finally(() => {
					this.loading = false
				})
		},
		getRowType(row) {
			return String(row.Type ?? "").toLowerCase()
		},
		getField(row, field) {
			const columnByField = {
				name: "Name",
				owner: "Owner",
				input: "Input",
				output: "Output",
				short: row.Short !== undefined
					? "Short"
					: (row.ShortDescription !== undefined ? "ShortDescription" : "Short_Description"),
			}
			const column = columnByField[field]
			return column && row[column] !== undefined && row[column] !== null ? row[column] : ""
		},
		getSteps(row) {
			const raw = row.Description ?? row.Steps ?? null
			if (!raw) return []

			if (Array.isArray(raw)) {
				return raw.map(step => String(step || "").trim()).filter(step => step !== "")
			}

			if (typeof raw === "string") {
				try {
					const parsed = JSON.parse(raw)
					if (Array.isArray(parsed)) {
						return parsed.map(step => String(step || "").trim()).filter(step => step !== "")
					}
				} catch (e) {
					// not JSON, fall through to newline splitting
				}
				return raw.split("\n").map(step => step.trim()).filter(step => step !== "")
			}

			return []
		},

		// --- Per-card edit ---
		startEdit(row) {
			if (this.editingRowId !== null) return
			this.rowBackup = { ...row }

			const steps = this.getSteps(row)
			this.editSteps = steps.length >= 3 ? [...steps] : [...steps, ...Array(3 - steps.length).fill("")]
			this.editType = this.getRowType(row) === "sub" ? "sub" : "main"
			this.editShort = this.getField(row, "short") || ""

			this.editingRowId = row.ID
		},
		cancelEdit(row) {
			if (this.rowBackup) {
				Object.assign(row, this.rowBackup)
			}
			this.editingRowId = null
			this.rowBackup = null
			this.editSteps = []
			this.editType = null
			this.editShort = ""
		},
		addEditStep() {
			if (this.editSteps.length < 10) {
				this.editSteps.push("")
			}
		},
		removeEditStep(index) {
			if (this.editSteps.length > 3) {
				this.editSteps.splice(index, 1)
			}
		},
		saveEdit(row, type) {
			if (!String(row.Name || "").trim()) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					new Error("Process name cannot be empty"),
					{ root: true }
				)
				return
			}
			if (this.editType !== "main" && this.editType !== "sub") {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					new Error("Process type must be 'main' or 'sub'"),
					{ root: true }
				)
				return
			}
			if (!Array.isArray(this.editSteps) || this.editSteps.length < 3 || this.editSteps.length > 10) {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					new Error("Each process must have between 3 and 10 description steps"),
					{ root: true }
				)
				return
			}

			const payload = [{
				id: row.ID,
				name: row.Name,
				owner: row.Owner,
				input: row.Input,
				output: row.Output,
				short: this.editShort,
				description: this.editSteps,
				type: this.editType,
				order: row.SortOrder || 1,
			}]

			this.savingRowId = row.ID
			console.log("print payload", payload)
			console.log("print type", type)

			riskAPI.updateProcesses(type, payload)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully updated process!`,
						{ root: true }
					)
					this.editingRowId = null
					this.rowBackup = null
					this.editSteps = []
					this.editType = null
					this.editShort = ""
					this.loadProcesses()
				})
				.catch(error => {
					this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
				})
				.finally(() => {
					this.savingRowId = null
				})
		},

		// --- PDF export ---
		downloadPdf() {
			if (!this.hasProcesses) return
			this.downloadingPdf = true

			try {
				const doc = new jsPDF({ unit: "pt", format: "a4" })
				const pageWidth = doc.internal.pageSize.getWidth()
				const pageHeight = doc.internal.pageSize.getHeight()
				const marginX = 40
				const marginBottom = 50
				const cardPadding = 14
				const colGap = 16
				const cardWidth = pageWidth - marginX * 2
				let y = 0

				const C = {
					primary: [30, 124, 131],
					primaryLight: [228, 243, 244],
					green: [76, 175, 80],
					greenLight: [237, 247, 237],
					dark: [45, 45, 45],
					gray: [140, 140, 140],
					white: [255, 255, 255],
				}

				const ensureSpace = needed => {
					if (y + needed > pageHeight - marginBottom) {
						doc.addPage()
						y = 50
					}
				}

				const wrap = (text, width, fontSize) => {
					doc.setFontSize(fontSize)
					doc.setFont(undefined, "normal")
					return doc.splitTextToSize(String(text ?? "").trim() || "—", width)
				}

				// Draws a label + wrapped value at (x, y); returns block height
				const drawField = (label, value, x, width) => {
					doc.setFontSize(8)
					doc.setFont(undefined, "bold")
					doc.setTextColor(...C.gray)
					doc.text(label.toUpperCase(), x, y)

					const lines = wrap(value, width, 10)
					doc.setFontSize(10)
					doc.setFont(undefined, "normal")
					doc.setTextColor(...C.dark)
					lines.forEach((line, i) => doc.text(line, x, y + 14 + i * 12))

					return 14 + lines.length * 12
				}

				const measureFieldHeight = (value, width) => 14 + wrap(value, width, 10).length * 12

				const stepBoxLayout = (steps, width) => {
					const cols = 3
					const gap = 10
					const boxWidth = (width - gap * (cols - 1)) / cols
					const wrapped = steps.map(s => wrap(s, boxWidth - 14, 8))
					const rows = []
					for (let i = 0; i < wrapped.length; i += cols) {
						const rowItems = wrapped.slice(i, i + cols)
						const maxLines = Math.max(...rowItems.map(l => l.length), 1)
						rows.push({ items: rowItems, height: Math.max(46, maxLines * 10 + 24) })
					}
					return { cols, gap, boxWidth, rows }
				}

				const stepsHeight = steps => {
					if (steps.length === 0) return 0
					const { rows } = stepBoxLayout(steps, cardWidth - cardPadding * 2)
					return 18 + rows.reduce((sum, r) => sum + r.height + 10, 0)
				}

				const drawSteps = (steps, x, width) => {
					doc.setFontSize(8)
					doc.setFont(undefined, "bold")
					doc.setTextColor(...C.gray)
					doc.text("PROCESS STEPS", x, y)
					y += 14

					const { cols, gap, boxWidth, rows } = stepBoxLayout(steps, width)
					rows.forEach((row, rIndex) => {
						row.items.forEach((lines, i) => {
							const bx = x + i * (boxWidth + gap)
							const stepNo = rIndex * cols + i + 1

							doc.setFillColor(...C.white)
							doc.setDrawColor(...C.primary)
							doc.setLineWidth(0.75)
							doc.roundedRect(bx, y, boxWidth, row.height, 6, 6, "FD")

							doc.setFillColor(...C.primary)
							doc.circle(bx + 13, y + 13, 7.5, "F")
							doc.setFontSize(7.5)
							doc.setFont(undefined, "bold")
							doc.setTextColor(...C.white)
							doc.text(String(stepNo), bx + 13, y + 15.5, { align: "center" })

							doc.setFontSize(8)
							doc.setFont(undefined, "normal")
							doc.setTextColor(...C.dark)
							lines.forEach((line, li) => doc.text(line, bx + 9, y + 26 + li * 10))
						})
						y += row.height + 10
					})
				}

				// --- Title ---
				y = 54
				doc.setFontSize(22)
				doc.setFont(undefined, "bold")
				doc.setTextColor(...C.primary)
				doc.text("Full Process View", pageWidth / 2, y, { align: "center" })
				y += 10
				doc.setDrawColor(...C.primary)
				doc.setLineWidth(1.4)
				doc.line(pageWidth / 2 - 55, y, pageWidth / 2 + 55, y)
				y += 18
				doc.setFontSize(9)
				doc.setFont(undefined, "normal")
				doc.setTextColor(...C.gray)
				doc.text(
					`Generated on ${new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}`,
					pageWidth / 2,
					y,
					{ align: "center" }
				)
				y += 30

				this.groups.forEach(group => {
					// --- Section header ---
					ensureSpace(42)
					const barH = 26
					doc.setFillColor(...C.primaryLight)
					doc.roundedRect(marginX, y, cardWidth, barH, 6, 6, "F")
					doc.setFontSize(12)
					doc.setFont(undefined, "bold")
					doc.setTextColor(...C.primary)
					doc.text(group.label, pageWidth / 2, y + barH / 2 + 4, { align: "center" })
					y += barH + 18

					group.rows.forEach((row, index) => {
						const steps = this.getSteps(row)
						const fullWidth = cardWidth - cardPadding * 2
						const col3Width = (fullWidth - colGap * 2) / 3
						const col2Width = (fullWidth - colGap) / 2
						const headerBlockHeight = 34

						const row1Height = Math.max(
							measureFieldHeight(this.getField(row, "name"), col3Width),
							measureFieldHeight(this.getField(row, "owner"), col3Width),
							measureFieldHeight(group.label.replace(" Processes", ""), col3Width)
						)
						const descHeight = measureFieldHeight(this.getField(row, "short"), fullWidth)
						const stepsBlockHeight = stepsHeight(steps)
						const row2Height = Math.max(
							measureFieldHeight(this.getField(row, "input"), col2Width),
							measureFieldHeight(this.getField(row, "output"), col2Width)
						)

						const totalCardHeight =
							headerBlockHeight +
							row1Height + 16 +
							descHeight + 16 +
							stepsBlockHeight + (steps.length ? 6 : 0) +
							row2Height +
							cardPadding * 2 +
							12

						// Fit whole card on one page where possible
						if (y + totalCardHeight > pageHeight - marginBottom && totalCardHeight < pageHeight - 100) {
							doc.addPage()
							y = 50
						} else {
							ensureSpace(60)
						}

						const cardTop = y

						// Header block
						doc.setFillColor(...C.greenLight)
						doc.roundedRect(marginX, y, cardWidth, headerBlockHeight, 8, 8, "F")
						doc.setFontSize(12)
						doc.setFont(undefined, "bold")
						doc.setTextColor(...C.primary)
						doc.text(`Process ${index + 1}`, marginX + cardPadding, y + headerBlockHeight / 2 + 4)
						y += headerBlockHeight + cardPadding

						// Row 1: Name / Owner / Type
						const rowStartY = y
						drawField("Process Name", this.getField(row, "name"), marginX + cardPadding, col3Width)
						y = rowStartY
						drawField("Process Owner", this.getField(row, "owner"), marginX + cardPadding + col3Width + colGap, col3Width)
						y = rowStartY
						drawField(
							"Process Type",
							group.label.replace(" Processes", "") + " Process",
							marginX + cardPadding + (col3Width + colGap) * 2,
							col3Width
						)
						y = rowStartY + row1Height + 16

						// Description
						drawField("Description of the Process", this.getField(row, "short"), marginX + cardPadding, fullWidth)
						y += descHeight + 16

						// Steps
						if (steps.length > 0) {
							drawSteps(steps, marginX + cardPadding, fullWidth)
							y += 6
						}

						// Row 2: Input / Output
						const row2StartY = y
						drawField("Input", this.getField(row, "input"), marginX + cardPadding, col2Width)
						y = row2StartY
						drawField("Output", this.getField(row, "output"), marginX + cardPadding + col2Width + colGap, col2Width)
						y = row2StartY + row2Height

						y += cardPadding

						// Card border, drawn last now that final height is known
						doc.setDrawColor(...C.green)
						doc.setLineWidth(1.2)
						doc.roundedRect(marginX, cardTop, cardWidth, y - cardTop, 8, 8, "S")

						y += 22
					})
				})

				// Footer on every page
				const pageCount = doc.internal.getNumberOfPages()
				for (let i = 1; i <= pageCount; i++) {
					doc.setPage(i)
					doc.setDrawColor(225, 225, 225)
					doc.setLineWidth(0.5)
					doc.line(marginX, pageHeight - 32, pageWidth - marginX, pageHeight - 32)
					doc.setFontSize(8)
					doc.setFont(undefined, "normal")
					doc.setTextColor(...C.gray)
					doc.text("ORIXE - ISO Process ", marginX, pageHeight - 20)
					doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 20, { align: "right" })
				}

				const dateStr = new Date().toISOString().slice(0, 10)
				doc.save(`process-view-${dateStr}.pdf`)
			} catch (error) {
				this.$store.dispatch("loading/" + LoadingState.ACTION_SET_ERROR, error, { root: true })
			} finally {
				this.downloadingPdf = false
			}
		},
	},
}
</script>

<style scoped>
.header-row {
	position: relative;
}

.download-pdf-btn {
	position: absolute;
	right: 0;
}

.section-title {
	color: #1e7c83;
	margin-bottom: 15px;
}

.process-entry-card {
	border: 2px solid #4caf50 !important;
	border-radius: 12px !important;
}

.type-change-hint {
	font-size: 13px;
	color: #b06a00;
	margin: 0 0 12px 0;
}

.field-row {
	display: flex;
	gap: 20px;
}

.field-block {
	flex: 1;
	min-width: 0;
}

.field-block .field-label {
	margin: 6px 0 0 0;
	font-weight: 500;
}

.field-block .field-value {
	margin: 0 0 4px 0;
	word-break: break-word;
	color: #333;
}

.description-grid {
	display: grid;
	grid-template-columns: repeat(5, minmax(0, 1fr));
	gap: 14px;
	margin-bottom: 16px;
}

.chevron-box {
	position: relative;
	height: 110px;
}

.chevron-inner {
	position: relative;
	background: #fff;
	border: 1px solid #1e7c83;
	border-radius: 10px;
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	padding: 8px;
	overflow: hidden;
}

.step-text-value {
	margin: 0;
	color: #333;
	line-height: 1.3;
	overflow-y: auto;
	max-height: 100%;
	width: 100%;
}

.chevron-textarea {
	width: 100%;
	height: 100%;
}

.chevron-textarea >>> .v-input__slot {
	background: transparent !important;
	box-shadow: none !important;
	border: none !important;
}

.chevron-textarea >>> .v-text-field__details {
	display: none !important;
}

.chevron-textarea >>> textarea {
	color: #333;
	cursor: text;
	resize: none;
}

.chevron-textarea >>> textarea::placeholder {
	color: #999;
}

.step-remove-btn {
	position: absolute;
	top: 2px;
	right: 2px;
}

@media (max-width: 900px) {
	.description-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

@media (max-width: 768px) {
	.field-row {
		flex-direction: column;
		gap: 0;
	}

	.description-grid {
		grid-template-columns: 1fr;
	}

	.header-row {
		flex-direction: column;
		gap: 12px;
	}

	.download-pdf-btn {
		position: static;
	}
}
</style>